// CustomerWelfareTransactionReport.jsx — "Show Available Welfare Amount" drill-down from Total Customers

import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptIcon from '@mui/icons-material/Receipt';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import { format, subDays } from 'date-fns';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../constants/constants';
// [CASH-MODE] UPI / Cash filter + indication
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeLabel, paymentModeParam, totalsByModeFromRows } from '../../utils/paymentMode';
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import CustomerReportShell, {
  DateRangeFilter,
  ReportGrid,
  SectionCard,
  displayVpa,
  formatINR,
  formatISTDateTime,
  parseApiDate,
  useCanViewCustomerSensitive,
  validateDateRange,
} from '../../components/CustomerReportShell';

const CustomerWelfareTransactionReport = () => {
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [transactions, setTransactions] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE] API totalsByMode when provided
  const canViewSensitive = useCanViewCustomerSensitive();

  const { customerId } = useParams();

  useEffect(() => {
    fetchWelfareTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, startDate, endDate, paymentMode]); // [CASH-MODE] refetch on mode change

  const fetchWelfareTransactions = async () => {
    const rangeError = validateDateRange(startDate, endDate);
    if (rangeError) {
      setDialogMessage(rangeError);
      setOpenDialog(true);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = sessionStorage.getItem('token');
      const { data } = await axios.post(
        `${API_BASE_URL}/customerReport/welfareTransactions`,
        {
          customerId,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          page: 1,
          limit: 1000,
          paymentMode: paymentModeParam(paymentMode), // [CASH-MODE] undefined for ALL
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setCustomerName(data.customerName || '');
      setTotalsByMode(data.totalsByMode || null); // [CASH-MODE]
      setTransactions((data.transactions || []).map((item, index) => ({
        id: index + 1,
        serialNumber: index + 1,
        customerName: data.customerName || '',
        billAmount: parseFloat(item.billAmount || 0),
        amount: parseFloat(item.amount || 0),
        rawDate: item.dateTime,
        dateTime: formatISTDateTime(item.dateTime),
        vpa: item.vpa || 'N/A',
        description: item.description || 'N/A',
        transactionId: item.transactionId || 'N/A',
        paymentMode: item.paymentMode, // [CASH-MODE]
      })));
      setGrandTotal(parseFloat(data.grandTotal || 0));
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(
        typeof err.response?.data?.message === 'string'
          ? err.response.data.message
          : 'Something went wrong while loading the welfare transactions. Please try again later.'
      );
      setTransactions([]);
      setGrandTotal(0);
      setTotalsByMode(null);
    }
    setLoading(false);
  };

  const totalBills = transactions.reduce((s, t) => s + t.billAmount, 0);
  // [CASH-MODE] welfare amount split by UPI / Cash
  const modeTotals = totalsByMode || totalsByModeFromRows(transactions, (t) => t.amount);
  const fmt4 = (n) => formatINR(n, 4);

  const handlePrint = () => {
    if (transactions.length === 0) {
      setDialogMessage('No data to print.');
      setOpenDialog(true);
      return;
    }
    const doc = new jsPDF('l', 'pt', 'a4');
    const center = doc.internal.pageSize.getWidth() / 2;
    doc.setFontSize(15);
    doc.text('Customer Welfare Transactions Report', center, 40, { align: 'center' });
    doc.setFontSize(10);
    if (customerName) doc.text(`Customer: ${customerName}`, center, 60, { align: 'center' });
    doc.text(`From ${format(startDate, 'dd-MM-yyyy')} to ${format(endDate, 'dd-MM-yyyy')}`, center, customerName ? 80 : 60, { align: 'center' });

    doc.autoTable({
      startY: customerName ? 90 : 70,
      head: [['S.No.', 'Customer', 'Bill Amount', 'Welfare Amount', 'Date & Time', 'VPA', 'Description', 'Transaction ID', 'Mode']],
      body: transactions.map((t) => [
        t.serialNumber,
        t.customerName,
        `Rs.${t.billAmount.toFixed(2)}`,
        `Rs.${t.amount.toFixed(4)}`,
        t.dateTime,
        displayVpa(t.vpa, canViewSensitive),
        t.description,
        String(t.transactionId),
        paymentModeLabel(t), // [CASH-MODE]
      ]),
      foot: [['', '', '', `Rs.${grandTotal.toFixed(4)}`, 'Grand Total', '', '', '', '']],
      theme: 'grid',
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
      styles: { fontSize: 9, overflow: 'linebreak' },
      margin: { right: 30, left: 30 },
    });
    doc.save('Customer_Welfare_Transactions_Report.pdf');
  };

  const columns = [
    { field: 'serialNumber', headerName: '#', width: 60 },
    { field: 'rawDate', headerName: 'Date & Time (IST)', width: 190, type: 'dateTime',
      valueGetter: (v) => parseApiDate(v), valueFormatter: (v) => formatISTDateTime(v) },
    { field: 'billAmount', headerName: 'Bill Amount', width: 140, type: 'number', valueFormatter: (v) => (v ? formatINR(v) : '-') },
    {
      field: 'amount', headerName: 'Welfare Amount', width: 160, type: 'number',
      renderCell: (p) => <Typography variant="body2" sx={{ fontWeight: 800, color: 'success.dark', width: '100%', textAlign: 'right' }}>{fmt4(p.value)}</Typography>,
    },
    // [CASH-MODE] UPI / Cash badge
    { field: 'paymentMode', headerName: 'Mode', width: 100, renderCell: (params) => <PaymentModeChip row={params.row} /> },
    { field: 'description', headerName: 'Description', width: 150 },
    { field: 'vpa', headerName: 'VPA', flex: 1, minWidth: 180, valueGetter: (v) => displayVpa(v, canViewSensitive) },
    {
      field: 'transactionId', headerName: 'Transaction ID', width: 220,
      renderCell: (p) => <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{String(p.value || '-')}</Typography>,
    },
  ];

  return (
    <CustomerReportShell
      title="Welfare History"
      subtitle={`Welfare amount credited on bills · ${format(startDate, 'dd MMM yyyy')} to ${format(endDate, 'dd MMM yyyy')}`}
      icon={<HealthAndSafetyIcon />}
      customerId={customerId}
      customerName={customerName}
      actions={(
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} disabled={transactions.length === 0} sx={{ fontWeight: 700 }}>
          Print
        </Button>
      )}
      filters={(
        <DateRangeFilter startDate={startDate} endDate={endDate} onStartChange={setStartDate} onEndChange={setEndDate}>
          {/* [CASH-MODE] */}
          <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
        </DateRangeFilter>
      )}
      error={error}
      onRetry={fetchWelfareTransactions}
      statsLoading={loading}
      stats={[
        { label: 'Welfare Earned', value: fmt4(grandTotal), hint: `${transactions.length} credit${transactions.length === 1 ? '' : 's'} in range`, color: 'success', icon: <HealthAndSafetyIcon /> },
        { label: 'Bills Covered', value: formatINR(totalBills), hint: 'Total bill amount', color: 'primary', icon: <ReceiptIcon /> },
        { label: 'Welfare · UPI', value: fmt4(modeTotals.UPI && modeTotals.UPI.amount), hint: `${(modeTotals.UPI && modeTotals.UPI.count) || 0} record(s)`, color: 'secondary', icon: <QrCode2Icon /> },
        { label: 'Welfare · Cash', value: fmt4(modeTotals.CASH && modeTotals.CASH.amount), hint: `${(modeTotals.CASH && modeTotals.CASH.count) || 0} record(s)`, color: 'warning', icon: <LocalAtmIcon /> },
      ]}
    >
      <SectionCard title="Welfare Transactions" subtitle={`${transactions.length} record${transactions.length === 1 ? '' : 's'}`}>
        <ReportGrid
          rows={transactions}
          columns={columns}
          loading={loading}
          emptyTitle="No welfare transactions found"
          emptyMessage="No welfare was credited to this customer in the selected dates."
        />
      </SectionCard>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Alert</DialogTitle>
        <DialogContent><Typography>{dialogMessage}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
    </CustomerReportShell>
  );
};

export default CustomerWelfareTransactionReport;
